import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useMessagingUnreadCount } from '../hooks/useFloatingMessaging'

/**
 * Synchronise la pastille de l'icône applicative avec le nombre de messages
 * non lus.
 *
 * C'est le seul signal qui subsiste quand l'application est **fermée** : sur
 * une PWA installée, la pastille s'affiche sur l'icône de l'écran d'accueil.
 * Le badge du dock suppose l'application ouverte, le titre d'onglet suppose un
 * onglet, la notification navigateur suppose une permission accordée.
 *
 * Même source que le dock et le titre (`useMessagingUnreadCount`) : les trois
 * surfaces ne peuvent pas diverger.
 *
 * `setAppBadge` n'existe qu'en contexte sécurisé et sur PWA installée ; partout
 * ailleurs le composant ne fait rien. Les appels renvoient une promesse qui
 * peut rejeter (permission, plateforme) — on l'avale volontairement : une
 * pastille est un confort, son échec ne doit rien casser.
 */
const AppBadge = () => {
  const totalUnread = useMessagingUnreadCount()
  // La pastille survit à la fermeture de l'application : elle ne doit jamais
  // rester affichée après une déconnexion.
  //
  // On ne peut pas compter sur la remise à zéro du singleton pour ça :
  // `FloatingMessagingManager` est monté conditionnellement à
  // l'authentification, donc React le démonte au logout et emporte l'écouteur
  // `user_logged_out` qui déclenche ce reset. Ce composant-ci est monté plus
  // haut et survit — il garderait alors la dernière valeur connue.
  //
  // On regarde donc l'utilisateur directement : sans session, pas de pastille,
  // quel que soit l'ordre de démontage.
  const isAuthenticated = Boolean(useSelector((state) => state.user?.user))

  useEffect(() => {
    if (typeof navigator === 'undefined' || typeof navigator.setAppBadge !== 'function') {
      return
    }

    if (isAuthenticated && totalUnread > 0) {
      navigator.setAppBadge(totalUnread).catch(() => {})
    } else {
      navigator.clearAppBadge?.().catch(() => {})
    }
  }, [totalUnread, isAuthenticated])

  return null
}

export default AppBadge
